import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React from 'react';

import { SortByMode } from '~/src/dash/components/material-search-dropdown-multi-list';
import { ORDER_DIRECTION, valueTypes } from '~/src/dash/components/tables/common';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { generateCountriesMenuList } from '~/src/dash/utils/countries';
import { createAllDeviceTypesMenuList } from '~/src/dash/utils/devices';
import {
  BrandsDashboardDynamicFilterConfigKey,
  BrandsDashboardFilterConfigKey,
  IBrandsDashboardConfig,
} from '~/src/dash/views/brands-dashboard/BrandsDashboard/common/types';
import {
  generateDemandSourcesList,
  getBidderGroupsList,
  getBiddersList,
  getChannelGroupsList,
  getSmallSitesList,
} from '~/src/dash/views/common/dashboards-consts';
import { TKey } from '~/src/dash/publicaUI';

// functions to reformat filters into the needed api params format
export const formatFilter = (
  menuTitle: BrandsDashboardFilterConfigKey | BrandsDashboardDynamicFilterConfigKey,
  selectedList: IMenuList = {},
) => {
  switch (menuTitle) {
    case 'Countries':
      // convert to uppercase string joined with commas
      return Object.keys(selectedList)
        .map((key) => "'" + key.toUpperCase() + "'")
        .join(',');
    // if all are selected, send back empty string, else convert to string with escapes
    case 'Device Types':
      if (
        isEmpty(selectedList) ||
        Object.keys(selectedList).length ===
          Object.keys(brandsDashboardConfig.filters[menuTitle].list).length
      ) {
        return '';
      } else {
        // convert to string with escapes
        return Object.keys(selectedList)
          .map((dT) => "'" + dT + "'")
          .join(',');
      }
    case 'Channels':
    case 'Bidders':
      // join ids by comma
      return Object.keys(selectedList).join(',');
    case 'Demand Sources':
    case 'Bidder Tiers':
      // convert to string with escapes
      if (isEmpty(selectedList)) {
        return '';
      }
      return Object.keys(selectedList)
        .map((dT) => "'" + dT + "'")
        .join(',');
    case 'Channel Groups':
      const channelIDs: string[] = [];
      Object.values(selectedList).forEach((channelGroup) => {
        channelIDs.push(...(channelGroup.channelGroup?.ChannelIDs.split(',') || []));
      });
      return channelIDs.join(',');
    case 'Bidder Groups':
      const bidderIDs: string[] = [];
      Object.values(selectedList).forEach((bidderGroup) => {
        bidderIDs.push(...(bidderGroup.bidderGroup?.BidderIDs.split(',') || []));
      });
      return bidderIDs.join(',');
    default:
      return '';
  }
};

const replaceSpecificDemandSourceKeys = (key: string): string => {
  switch (key) {
    case 'unruly':
      return 'rhythmone';
    case 'xandr':
      return 'appnexus';
    case 'azerion':
      return 'improvedigital';
    default:
      return key;
  }
};

export const formatComparisonKeys = (keys: string[], apiKey: string) => {
  switch (apiKey) {
    // convert to string with escapes
    case 'Adomain':
    case 'BidderType':
      // check to see if any demand source keys need to be replaced
      return keys
        .map((key) => "'" + replaceSpecificDemandSourceKeys(key).replaceAll("'", "''") + "'")
        .join(',');
    // convert to string with no escapes
    case 'LineItemID':
    case 'BidderID':
      return keys.join(',');
    default:
      return null;
  }
};

const generateBidderTierList = (tiers: number) => {
  const bidderTierList: IMenuList = {};
  for (let i = 1; i <= tiers; i++) {
    bidderTierList[i] = {
      ID: `${i}`,
      title: `Tier ${i}`,
      selected: false,
    };
  }
  return bidderTierList;
};

const grayHighlightedHeaderStyle = {
  padding: '2px 6px',
  borderRadius: '6px',
  backgroundColor: publicaColors.veryLightGray,
  color: publicaColors.darkGray,
} as React.CSSProperties;

// IMPORTANT!!! READ ME BEFORE UPDATING CONFIG
// if updating filters, dynamic filters... make sure to update the formatFilter function above
// if updating main metrics... make sure to update the formatComparisonKeys function above

export const brandsDashboardConfig: IBrandsDashboardConfig = {
  mainData: {
    graphID: 83,
  },
  totalRevenue: {
    graphID: 84,
  },
  calendar: {
    // this is the cut off date because there is no data before January 12, 2023
    minDate: moment('12-01-2023', 'DD-MM-YYYY'),
  },
  // these are static filters requiring no props
  // NOTE: MENUTITLE MUST MATCH OBJECT KEY
  filters: {
    'Device Types': {
      menuTitle: 'Device Types',
      list: createAllDeviceTypesMenuList(),
      singularString: 'Device Type',
      paramKey: 'devicetypes',
      tKey: 'DeviceTypes',
    },
    Countries: {
      menuTitle: 'Countries',
      list: generateCountriesMenuList(),
      singularString: 'Country',
      paramKey: 'countries',
      tKey: 'Countries',
    },
    'Bidder Tiers': {
      menuTitle: 'Bidder Tiers',
      list: generateBidderTierList(10),
      singularString: 'Bidder Tier',
      paramKey: 'bidder_tiers',
      sortMode: SortByMode.IdAsc,
      tKey: 'BidderTiers',
    },
  },
  // these filters require session & user props in order to be populated
  // NOTE: MENUTITLE MUST MATCH OBJECT KEY
  dynamicFilters: {
    'Demand Sources': {
      menuTitle: 'Demand Sources',
      singularString: 'Demand Source',
      paramKey: 'biddercodes',
      getList: (session: ISessionState, user: IUserState) =>
        generateDemandSourcesList(session, user),
      tKey: 'DemandSources',
    },
    Bidders: {
      menuTitle: 'Bidders',
      singularString: 'Bidder',
      paramKey: 'bidder_ids',
      getList: (session: ISessionState, user: IUserState) => getBiddersList(session, user),
      tKey: 'Bidders',
    },
    'Bidder Groups': {
      menuTitle: 'Bidder Groups',
      singularString: 'Bidder Group',
      paramKey: 'bidder_ids',
      getList: (session: ISessionState, user: IUserState) => getBidderGroupsList(session, user),
      tKey: 'BidderGroups',
    },
    Channels: {
      menuTitle: 'Channels',
      singularString: 'Channel',
      paramKey: 'site_ids',
      getList: (session: ISessionState, user: IUserState) => getSmallSitesList(session, user),
      tKey: 'Channels',
    },
    'Channel Groups': {
      menuTitle: 'Channel Groups',
      singularString: 'Channel Group',
      paramKey: 'site_ids',
      getList: (session: ISessionState, user: IUserState) => getChannelGroupsList(session, user),
      tKey: 'ChannelGroups',
    },
  },
  // NOTE: LABEL MUST MATCH OBJECT KEY
  // when adding new groupBys, make sure to add the keys to ITableGroupByKey
  groupBys: {
    Advertiser: {
      label: 'Advertiser',
      pluralLabel: 'Advertisers',
      apiKey: 'Adomain',
      displayKey: 'Adomain',
      requestKey: 'adomain',
      matchKey: 'adomains',
      isSelectable: true,
      isTableControllingSort: true,
      isViewBy: true,
      isDefault: true,
      drillPath: ['Demand Source', 'Bidder'],
      tKey: 'Advertiser',
    },
    'Demand Source': {
      label: 'Demand Source',
      pluralLabel: 'Demand Sources',
      apiKey: 'BidderType',
      displayKey: 'BidderTypeName',
      requestKey: 'bidder_type',
      matchKey: 'biddercodes',
      isSelectable: true,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: ['Advertiser'],
      tKey: 'DemandSource',
    },
    Bidder: {
      label: 'Bidder',
      pluralLabel: 'Bidders',
      apiKey: 'BidderID',
      displayKey: 'BidderName',
      requestKey: 'bidder_id',
      matchKey: 'bidder_ids',
      isSelectable: true,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: ['Advertiser'],
      tKey: 'Bidder',
    },
  },
  mainMetrics: {
    // ORDER MATTERS HERE
    // NOTE: LABEL MUST MATCH OBJECT KEY
    // when adding new metrics, make sure to add the keys to ITableMetricKey
    'Total Revenue': {
      label: 'Total Revenue',
      apiKey: 'Revenue',
      requestKey: 'revenue',
      valueType: valueTypes.CURRENCY,
      isDefault: true,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
    },
    'Total Revenue Compare': {
      label: 'Total Revenue Compare',
      alias: '%',
      apiKey: 'Revenue',
      requestKey: 'revenue',
      valueType: valueTypes.PERCENT_CHANGE,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      isCompareValue: true,
      tooltipMessage: 'Total Revenue Change From Comparison Window',
      headerStyle: grayHighlightedHeaderStyle,
    },
    CPM: {
      label: 'CPM',
      apiKey: 'AvgImpECPM',
      requestKey: 'avg_imp_ecpm',
      valueType: valueTypes.CURRENCY,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
    },
    'CPM Compare': {
      label: 'CPM Compare',
      alias: '%',
      apiKey: 'AvgImpECPM',
      requestKey: 'avg_imp_ecpm',
      valueType: valueTypes.PERCENT_CHANGE,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      isCompareValue: true,
      tooltipMessage: 'CPM Change From Comparison Window',
      headerStyle: grayHighlightedHeaderStyle,
    },
    Impressions: {
      label: 'Impressions',
      apiKey: 'Impressions',
      requestKey: 'impressions',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
    },
    'Impressions Compare': {
      label: 'Impressions Compare',
      alias: '%',
      apiKey: 'Impressions',
      requestKey: 'impressions',
      valueType: valueTypes.PERCENT_CHANGE,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      isCompareValue: true,
      tooltipMessage: 'Impressions Change From Comparison Window',
      headerStyle: grayHighlightedHeaderStyle,
    },
  },
  table: {
    defaultRowsPerPage: 10,
    rowsPerPageOptions: [10, 25, 50],
    totalLimit: 100,
  },
};

export const dateRangeStringToTKey: Record<string, TKey> = {
  Yesterday: 'Yesterday',
  'This Week(Sun-Yesterday)': 'ThisWeekSunToYesterday',
  'Last 7 Days': 'LastSevenDays',
  'Last Week(Sun-Sat)': 'LastWeekSunToSat',
  'This Month': 'ThisMonth',
  'Last 30 Days': 'LastThirtyDays',
  'Last Month': 'LastMonth',
};
